import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ChoiceChips from 'components/Form/choiceChips';
import DateRangeFields from 'components/Form/dateRangeFields';
import FilterDrawerShell from 'components/Form/filterDrawerShell';
import FilterSection, { FilterField } from 'components/Form/filterSection';
import FilterSelect from "components/Form/filterSelect";
import { countActive, filterValueOf, resolveMultiValue } from 'components/Form/filterValues';
import { documentExpiresInOptions, documentExpiryStatusOptions, documentStatusOptions } from 'utils/constants';

const employeeStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "All", value: "all" },
];

// Same values as documentExpiryStatusOptions; reworded so this is not a
// second "Status" next to the record status.
const validityOptions = documentExpiryStatusOptions.map((option) =>
  option.value === "active" ? { ...option, label: "Not expired" } : option
);

const SECTION_KEYS = {
  expiry: ["expiry_status", "expiry_in", "expiry_date_after", "expiry_date_before"],
  document: ["document_type", "document_status", "issue_date_after", "issue_date_before"],
  employee: ["company_user", "cost_center", "employee_status"],
};

const DocumentsFilterDrawer = ({
    handleFilterDrawer,
    showFilterDrawer,
    handleFilterChange,
    filters,
    handleApplyFilter,
    filterLoading,
    handleDateChange,
    documentType,
    storeType,
    handleClearAll,
    isFilterDirty,
    applyFilterValues
}) => {
  const { userData, userDataLoading, costCenter, costCenterLoading } = useSelector((state) => state.common);
  const [userDataOption, setUserDataOption] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [costCenterOption, setCostCenterOption] = useState([]);

  useEffect(() => {
    setDocumentTypeOptions((documentType || []).map(item => ({ label: item.name, value: item.id })));
  }, [documentType]);

  useEffect(() => {
    setCostCenterOption((costCenter || []).map(item => ({
      label: item.name,
      value: item.id,
      // Not shown, but FilterSelect's filterOption matches it, so typing a
      // store code still finds the option.
      code: item.code
    })));
    setUserDataOption((userData || []).map(item => {
      const name = `${item.first_name || ""} ${item.last_name || ""}`.trim();
      return {
        label: `${item.emp_no ? item.emp_no + " - " : ""}${name}${item.is_active === false ? " (Inactive)" : ""}`,
        value: item.id,
        emp_no: item.emp_no,
        email: item.email
      };
    }));
  }, [userData, costCenter]);

  const valueOf = (key) => filterValueOf(filters, key);
  const isStore = storeType === "store";
  const sectionKeys = isStore
    ? { expiry: SECTION_KEYS.expiry, document: SECTION_KEYS.document }
    : SECTION_KEYS;
  const activeCount = countActive(filters, Object.values(sectionKeys).flat());

  // Expired documents have no "expires within" window, so the two expiry
  // choices are exclusive: picking one clears the other when they clash.
  const setMany = (entries) => (applyFilterValues ? applyFilterValues(entries, false) : entries.forEach(({ key, data }) => handleFilterChange(data, key)));
  const onExpiryStatus = (option) =>
    setMany([{ key: "expiry_status", data: option }, ...(option?.value === "expired" ? [{ key: "expiry_in", data: "" }] : [])]);
  const onExpiresIn = (option) =>
    setMany([{ key: "expiry_in", data: option }, ...(option && valueOf("expiry_status") === "expired" ? [{ key: "expiry_status", data: "" }] : [])]);

  return (
    <FilterDrawerShell
      open={showFilterDrawer}
      onClose={handleFilterDrawer}
      activeCount={activeCount}
      onClearAll={handleClearAll}
      onApply={handleApplyFilter}
      applying={filterLoading}
      applyDisabled={!isFilterDirty}
    >
      {!isStore && (
        <FilterSection title="Employee" count={countActive(filters, SECTION_KEYS.employee)}>
          <FilterField label="Employee" hint="one or more" htmlFor="filter-document-user">
            <FilterSelect inputId="filter-document-user" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "company_user")}} optionLabel={"label"} optionValue={'value'} options={userDataOption} isLoading={userDataLoading} value={resolveMultiValue(valueOf("company_user"), userDataOption)} placeholder={"Any"}/>
          </FilterField>
          <FilterField label="Cost center" hint="one or more" htmlFor="filter-document-cost-center">
            <FilterSelect inputId="filter-document-cost-center" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "cost_center")}} optionLabel={"label"} optionValue={'value'} options={costCenterOption} isLoading={costCenterLoading} value={resolveMultiValue(valueOf("cost_center"), costCenterOption)} placeholder={"Any"} noOptionsLabel={"cost centers"}/>
          </FilterField>
          <FilterField label="Employment" hint="default: active employees">
            <ChoiceChips ariaLabel="Employment status" options={employeeStatusOptions} value={valueOf("employee_status")} onChange={(option) => handleFilterChange(option, "employee_status")} />
          </FilterField>
        </FilterSection>
      )}

      <FilterSection title="Document" count={countActive(filters, SECTION_KEYS.document)}>
        <FilterField label="Type" hint="one or more" htmlFor="filter-document-type">
          <FilterSelect inputId="filter-document-type" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "document_type")}} optionLabel={"label"} optionValue={'value'} options={documentTypeOptions} value={resolveMultiValue(valueOf("document_type"), documentTypeOptions)} placeholder={"Any"} noOptionsLabel={"document types"}/>
        </FilterField>
        <FilterField label="Record" hint="default: active records">
          <ChoiceChips ariaLabel="Record status" options={documentStatusOptions} value={valueOf("document_status")} onChange={(option) => handleFilterChange(option, "document_status")} />
        </FilterField>
        <DateRangeFields
          label="Issued"
          fromId="issue_date_after"
          toId="issue_date_before"
          fromValue={valueOf("issue_date_after")}
          toValue={valueOf("issue_date_before")}
          onFromChange={(v) => handleDateChange(v, "issue_date_after")}
          onToChange={(v) => handleDateChange(v, "issue_date_before")}
        />
      </FilterSection>

      <FilterSection title="Expiry" count={countActive(filters, SECTION_KEYS.expiry)}>
        <FilterField label="Validity">
          <ChoiceChips ariaLabel="Validity" options={validityOptions} value={valueOf("expiry_status")} onChange={onExpiryStatus} />
        </FilterField>
        <FilterField label="Expires within" hint="counted from today">
          <ChoiceChips ariaLabel="Expires within" options={documentExpiresInOptions} value={valueOf("expiry_in")} onChange={onExpiresIn} />
        </FilterField>
        <DateRangeFields
          label="Expiry date"
          fromId="expiry_date_after"
          toId="expiry_date_before"
          fromValue={valueOf("expiry_date_after")}
          toValue={valueOf("expiry_date_before")}
          onFromChange={(v) => handleDateChange(v, "expiry_date_after")}
          onToChange={(v) => handleDateChange(v, "expiry_date_before")}
        />
      </FilterSection>
    </FilterDrawerShell>
  );
};
export default DocumentsFilterDrawer;
